import get from "lodash/get";
import React from "react";
import { Button, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import { useGetAccountingPeriodsGetterQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useUser } from "~/shared/context/UserContext";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";
import { DefaultRowControl } from "~/shared/ui/TableV2/ui/DefaultRowControl";

import AccountingPeriodModal from "./modals/AccountingPeriodModal";
import {
  ACCOUNTING_SORTABLE_COLUMNS,
  getAccountingPeriodColumns,
} from "./model/tableAdapter";

import s from "./Accounting.less";

const Accounting = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const { dealerChoice } = useUser();
  const { hasPermission } = usePermissions();
  const currentDealerId = useCurrentDealerId();
  const {
    count,
    page,
    currentId,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();

  // Бэкенд AccountingGetter.getPeriods не принимает order/orderBy,
  // поэтому сортировка клиентская, а в URL хранится только её состояние
  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: ACCOUNTING_SORTABLE_COLUMNS,
    setData,
  });

  const tableSortField =
    sortField && ACCOUNTING_SORTABLE_COLUMNS.includes(sortField)
      ? sortField
      : undefined;

  const onYearClick = (record: any) => {
    dealerChoice === "one"
      ? history.push(`/accounting/${record.id}/${currentDealerId}`)
      : history.push(`/accounting/${record.id}`);
  };

  const { data, refetch, loading } = useGetAccountingPeriodsGetterQuery({
    variables: {
      page,
      count,
    },
  });

  const accountingsData = get(
    data,
    "AccountingGetter.getPeriods.items",
    [],
  ).map((el) => {
    return {
      ...el,
      lastDate: getFormatDate(el.lastDate, true),
      lastDateRaw: el.lastDate ? new Date(el.lastDate).getTime() : 0,
      reportDate: getFormatDate(el.reportDate, true),
      reportDateRaw: el.reportDate ? new Date(el.reportDate).getTime() : 0,
    };
  });
  const accountingsTotal = get(data, "AccountingGetter.getPeriods.total", 0);

  const selectedAccountingsData = accountingsData.find(
    (i) => i.id === currentId,
  );

  const openCreateModal = () => setDataReplace("openModal", "Create");

  const handleRowEdit = (record: { id: string | number }) =>
    setDataReplace("openModal", "Edit", "currentId", record.id);

  const { handleCount, handlePage } = usePaginationParams();

  return (
    <Wrapper.Content
      title={t("accounting.period_title")}
      controlComponent={
        hasPermission("accounting.create") ? (
          <div className={s.controlsBlock}>
            <Button
              onClick={openCreateModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("accounting.newPeriod")}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className={s.paginationWrap}>
        <TableV2
          wrapperClassName={s.tableWrapper}
          columns={getAccountingPeriodColumns(
            t,
            onYearClick,
            tableSortField,
            sortType,
          )}
          dataSource={accountingsData}
          loading={loading}
          rowKey="id"
          scroll={{ x: "max-content" }}
          rowActions={(record) => (
            <DefaultRowControl
              record={record}
              onRowEdit={
                hasPermission("accounting.changePeriod")
                  ? handleRowEdit
                  : undefined
              }
            />
          )}
          actionsFixed
          sortableColumns={ACCOUNTING_SORTABLE_COLUMNS}
          sortField={tableSortField}
          sortType={tableSortField ? sortType : undefined}
          onSortChange={onSortChange}
          actionsPlacement={"left"}
        />
        <Pagination
          count={count}
          page={page + 1}
          totalCount={accountingsTotal}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
      </div>
      <AccountingPeriodModal
        selectedPeriod={selectedAccountingsData}
        reFetchTable={refetch}
      />
    </Wrapper.Content>
  );
};

export default Accounting;
